import React, { useState, useMemo, useCallback } from 'react'
import {
  Card,
  Typography,
  Space,
  Tag,
  Button,
  Collapse,
  Statistic,
  Row,
  Col,
  Alert,
  message
} from 'antd'
import {
  TableOutlined,
  CodeOutlined,
  ClockCircleOutlined,
  DatabaseOutlined,
  DownloadOutlined,
  BarChartOutlined,
  InfoCircleOutlined,
  ClearOutlined
} from '@ant-design/icons'
import { QueryResult } from '../types/api'
import DataChart from './DataChart'
import DataTable from './browse/DataTable'
import SearchBar from './browse/SearchBar'
import { FIELD_MAPPING } from '../config/field-mapping'
import { DataFormatter } from '../utils/formatters'
import type { FilterCondition } from '../types/field-mapping'

const { Title, Text, Paragraph } = Typography
const { Panel } = Collapse

interface QueryResultsProps {
  result: QueryResult | null
  loading: boolean
  onClearResults?: () => void
}

const QueryResults: React.FC<QueryResultsProps> = ({ result, loading, onClearResults }) => {
  const [activeKey, setActiveKey] = useState<string[]>(['data'])
  const [showChart, setShowChart] = useState(false)

  // 筛选和搜索状态
  const [searchText, setSearchText] = useState('')
  const [filters, setFilters] = useState<FilterCondition[]>([])
  const [searchHistory, setSearchHistory] = useState<string[]>([])

  // 处理搜索历史
  const handleSearchHistoryAdd = useCallback((text: string) => {
    const newHistory = [text, ...searchHistory.filter(item => item !== text)].slice(0, 10)
    setSearchHistory(newHistory)
    localStorage.setItem('query_search_history', JSON.stringify(newHistory))
  }, [searchHistory])

  // 清除搜索和筛选
  const handleClear = useCallback(() => {
    setSearchText('')
    setFilters([])
  }, [])

  // 处理数据和列定义 - 移到所有hooks的顶部
  const { processedData, columns } = useMemo(() => {
    if (!result?.data || result.data.length === 0) {
      return { processedData: [], columns: [] }
    }

    // 生成列定义，兼容DataTable格式
    const cols = Object.keys(result.data[0]).map(key => {
      const fieldConfig = FIELD_MAPPING[key]
      return {
        title: fieldConfig?.label || key,
        dataIndex: key,
        key: key,
        width: fieldConfig?.width || 120,
        sortable: true,
        filterable: ['factoryName', 'costType', 'month'].includes(key),
        searchable: fieldConfig?.searchable || false,
        fieldConfig: fieldConfig,
        render: (value: any) => {
          if (fieldConfig) {
            return DataFormatter.formatValue(value, fieldConfig)
          }
          if (typeof value === 'number') {
            return value.toLocaleString()
          }
          return value
        }
      }
    })

    // 应用搜索和筛选
    let filtered = result.data

    // 应用搜索
    if (searchText) {
      filtered = filtered.filter(item =>
        Object.values(item).some(value =>
          String(value).toLowerCase().includes(searchText.toLowerCase())
        )
      )
    }

    // 应用筛选
    if (filters.length > 0) {
      filtered = filtered.filter(item => {
        return filters.every(filter => {
          const value = item[filter.field]
          switch (filter.operator) {
            case 'eq':
              return value === filter.value
            case 'like':
              return String(value).toLowerCase().includes(String(filter.value).toLowerCase())
            default:
              return true
          }
        })
      })
    }

    return { processedData: filtered, columns: cols }
  }, [result?.data, searchText, filters])

  const handleExport = useCallback(() => {
    if (!result?.data || result.data.length === 0) {
      message.warning('没有数据可导出')
      return
    }

    // 简单的 CSV 导出
    const headers = Object.keys(result.data[0])
    const csvContent = [
      headers.join(','),
      ...result.data.map(row =>
        headers.map(header => `"${row[header] || ''}"`).join(',')
      )
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `查询结果_${new Date().toISOString().slice(0, 10)}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    message.success('数据导出成功')
  }, [result?.data])

  if (loading) {
    return (
      <Card className="result-card">
        <div className="loading-container">
          <div>正在执行查询...</div>
        </div>
      </Card>
    )
  }

  if (!result) {
    return (
      <Card className="result-card">
        <div className="empty-container">
          <DatabaseOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
          <div>请输入查询内容开始分析</div>
        </div>
      </Card>
    )
  }

  if (!result.success) {
    return (
      <Card className="result-card">
        <Alert
          message="查询失败"
          description={result.error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
        {result.sql && (
          <Collapse>
            <Panel header="生成的 SQL" key="sql">
              <pre style={{
                background: '#f5f5f5',
                padding: 12,
                borderRadius: 4,
                overflow: 'auto'
              }}>
                {result.sql}
              </pre>
            </Panel>
          </Collapse>
        )}
      </Card>
    )
  }

  // 现在所有hooks都在条件渲染之前，继续处理成功的结果

  return (
    <Card className="result-card">
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={4} style={{ margin: 0 }}>
            <TableOutlined style={{ marginRight: 8, color: '#52c41a' }} />
            查询结果
          </Title>
          
          <Space>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExport}
              disabled={!result.data || result.data.length === 0}
            >
              导出数据
            </Button>
            <Button
              icon={<BarChartOutlined />}
              disabled={!result.data || result.data.length === 0}
              onClick={() => setShowChart(!showChart)}
              type={showChart ? 'primary' : 'default'}
            >
              {showChart ? '隐藏图表' : '生成图表'}
            </Button>
            {onClearResults && (
              <Button
                icon={<ClearOutlined />}
                onClick={onClearResults}
                danger
                type="text"
              >
                清除结果
              </Button>
            )}
          </Space>
        </div>

        {/* 统计信息 */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Statistic
              title="查询结果"
              value={`${processedData.length}${processedData.length !== (result.total_rows || 0) ? ` / ${result.total_rows}` : ''}`}
              suffix="条记录"
              prefix={<DatabaseOutlined />}
            />
          </Col>
          <Col span={6}>
            <Statistic 
              title="执行时间" 
              value={result.execution_time ? (result.execution_time * 1000).toFixed(2) : 0} 
              suffix="ms"
              prefix={<ClockCircleOutlined />}
            />
          </Col>
          <Col span={6}>
            <Statistic 
              title="查询方式" 
              value={result.method === 'ai_generation' ? 'AI生成' : '直接SQL'}
              prefix={<InfoCircleOutlined />}
            />
          </Col>
          <Col span={6}>
            <Statistic 
              title="数据列数" 
              value={columns.length} 
              suffix="列"
              prefix={<TableOutlined />}
            />
          </Col>
        </Row>

        {/* 查询信息 */}
        <Alert
          message={
            <Space>
              <Text strong>原始查询:</Text>
              <Text code>{result.query}</Text>
            </Space>
          }
          type="info"
          style={{ marginBottom: 16 }}
        />
      </div>

      {/* 图表显示 */}
      {showChart && result.data && result.data.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <DataChart
            data={result.data}
            title={`${result.query} - 数据可视化`}
          />
        </div>
      )}

      <Collapse
        activeKey={activeKey}
        onChange={setActiveKey}
        style={{ marginBottom: 16 }}
      >
        {/* 数据表格 */}
        <Panel
          header={
            <Space>
              <TableOutlined />
              数据表格
              <Tag color="blue">{processedData.length} 条记录</Tag>
              {processedData.length !== (result.total_rows || 0) && (
                <Tag color="orange">已筛选</Tag>
              )}
            </Space>
          }
          key="data"
        >
          {/* 搜索和筛选栏 */}
          <div style={{ marginBottom: 16 }}>
            <SearchBar
              onSearch={setSearchText}
              onFilter={setFilters}
              onClear={handleClear}
              loading={false}
              placeholder="搜索查询结果..."
              searchHistory={searchHistory}
              onSearchHistoryAdd={handleSearchHistoryAdd}
              showQuickFilters={true}
            />
          </div>

          {/* 数据表格 */}
          <DataTable
            columns={columns}
            dataSource={processedData.map((item, index) => ({ ...item, key: index }))}
            loading={false}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total: number, range: [number, number]) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`
            }}
            size="small"
            showColumnConfig={true}
          />
        </Panel>

        {/* SQL 查询 */}
        {result.sql && (
          <Panel 
            header={
              <Space>
                <CodeOutlined />
                生成的 SQL
                <Tag color="green">已验证</Tag>
              </Space>
            } 
            key="sql"
          >
            <pre style={{ 
              background: '#f5f5f5', 
              padding: 16, 
              borderRadius: 6,
              overflow: 'auto',
              fontSize: 13,
              lineHeight: 1.5
            }}>
              {result.sql}
            </pre>
          </Panel>
        )}

        {/* AI 解释 */}
        {result.explanation && (
          <Panel 
            header={
              <Space>
                <InfoCircleOutlined />
                AI 解释
              </Space>
            } 
            key="explanation"
          >
            <Paragraph style={{ margin: 0 }}>
              {result.explanation}
            </Paragraph>
          </Panel>
        )}
      </Collapse>
    </Card>
  )
}

export default QueryResults
